import { PaperClipOutlined } from '@ant-design/icons'
import { useQueries } from '@tanstack/react-query'
import { DatePicker, Divider, Flex, Table, Typography } from 'antd'
import { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import {
  useCounterpartServiceGetInternalInstitutionEndpointKey,
  useInvestigationsServiceGetPatientInvestigationTestsEndpoint
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  CounterpartService,
  InvestigationTestsListItemDto,
  InvestigationTestSortBy,
  InvestigationTestStatus
} from 'src/api/requests'
import {
  PageBreadcrumbs,
  SearchBar,
  Select,
  ClassifierInfiniteSelect,
  TbTag,
  Text
} from 'src/components'
import { getFullName } from 'src/features/users/utils'
import {
  onDownloadFile,
  useClassifierId,
  useDefaultSortingItem,
  useTablePagination
} from 'src/hooks'
import {
  dayMonthYearBackSlash,
  dayMonthYearHourMinutesFormatWithDot,
  formatDate
} from 'src/libs'
import { useLocales } from 'src/locales'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

import { usePatientConfig } from '../utils'

export const PatientTestResults = () => {
  const { t, currentLang } = useLocales()
  const pagination = useTablePagination()

  const { defaultSortingOption } = useDefaultSortingItem()
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('')
  const [date, setDate] = useState<string[]>([])
  const [status, setStatus] = useState<InvestigationTestStatus>()
  const { orderingOptions } = usePatientConfig()

  const [typeId, setTypeId] = useState('')

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering) as InvestigationTestSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(typeId && { typeId }),
    ...(status && { status }),
    ...(date?.[0] &&
      date?.[1] && {
        resultDateFrom: date?.[0],
        resultDateTo: date?.[1]
      })
  }

  const classifierId = useClassifierId(
    ClassifierType.TEST_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )

  const { data, isLoading } =
    useInvestigationsServiceGetPatientInvestigationTestsEndpoint(queryParams)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const institutionTypes = useQueries({
    queries:
      data?.data?.map((item) => ({
        queryKey: [
          useCounterpartServiceGetInternalInstitutionEndpointKey,
          String(item?.testedAt)
        ],
        queryFn: async () => {
          const response =
            await CounterpartService.getInternalInstitutionEndpoint(
              String(item?.testedAt)
            )

          return response
        }
      })) || []
  })

  const columns: ColumnType<InvestigationTestsListItemDto>[] = [
    {
      key: 'code',
      fixed: 'left',
      title: t('investigations.test_number'),
      render: (_, { code, isTb }) => (
        <Flex gap={8} align="center">
          {code}
          <TbTag isTb={isTb} />
        </Flex>
      )
    },
    {
      key: 'creation date',
      title: t('consultations.harvest_date'),
      render: (_, { createdAt }) =>
        createdAt
          ? dayjs
              .utc(createdAt)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'type',
      title: t('tests_results.type'),
      render: (_, { type }) => type?.translation?.[currentLang.value]
    },
    {
      key: 'tests_results',
      title: t('tests_results.test_date'),
      render: (_, { testingAt }) =>
        testingAt ? formatDate(testingAt, dayMonthYearBackSlash) : '-'
    },
    {
      key: 'result date',
      title: t('tests_results.result_date'),
      render: (_) => '-'
      // result?.resultDate ? formatDate(result?.resultDate) : '-'
    },
    {
      key: 'documents',
      title: t('stock_applications.uploaded_documents'),
      render: (_, { results }) => {
        const mediaFiles = results?.flatMap((r) => r.mediaItems ?? []) ?? []

        if (mediaFiles.length === 0) return '-'

        return (
          <Flex vertical gap={2}>
            {mediaFiles.map((item) => (
              <Typography.Link
                key={item.id}
                className="fit-content"
                onClick={() => onDownloadFile(t, item.id, item.name)}
              >
                <Flex gap={4}>
                  <PaperClipOutlined />
                  {item.name}
                </Flex>
              </Typography.Link>
            ))}
          </Flex>
        )
      }
    },
    {
      key: 'result',
      title: t('analysis.result'),
      render: (_, { results }) => {
        if (!results || results.length === 0) return '-'

        return (
          <Flex vertical gap={8}>
            {results.map((result, idx) => {
              const {
                resultValue,
                resultOption,
                resultValueOption,
                resultDate
              } = result

              const label =
                resultOption?.translation?.[currentLang?.value] ??
                resultValueOption?.translation?.[currentLang?.value] ??
                (typeof resultValue === 'number' ? resultValue : null)

              const formattedDate = resultDate
                ? dayjs
                    .utc(resultDate)
                    .tz()
                    .format(dayMonthYearHourMinutesFormatWithDot)
                : null

              return (
                <div key={idx}>
                  <div>{label ?? '-'}</div>
                  {formattedDate && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formattedDate}
                    </Text>
                  )}
                </div>
              )
            })}
          </Flex>
        )
      }
    },
    {
      key: 'result date',
      title: t('tests_results.result_date'),
      render: (_, { testedAt }) => {
        const instName = institutionTypes.find(
          (item) => item.data?.id === testedAt
        )

        return instName ? instName?.data?.name : '-'
      }
    },
    {
      key: 'referral doctor',
      title: t('investigations.referral_doctor'),
      render: (_, { referralDoctorName, referralDoctorUser }) =>
        referralDoctorName ||
        getFullName(
          referralDoctorUser?.firstName,
          referralDoctorUser?.lastName
        ) ||
        '-'
    }
  ]

  const resultsOptions = [
    defaultSortingOption
    // ...Object.values(InvestigationTestResultStatus).map((status) => ({
    //   value: status,
    //   label: t(`tests_results.${status}`)
    // }))
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.tests_results')}
      items={[{ title: t('menu.tests_results') }]}
    >
      <Flex justify="space-between" align="center" gap={8}>
        <SearchBar
          className="full-width"
          placeholder={t('investigations.search_by_person_code')}
          onSearch={onSearch}
        />

        <ClassifierInfiniteSelect
          hasDefault
          className="full-width"
          id={classifierId}
          placeholder={t('tests_results.type')}
          onSelect={(val: string) => setTypeId(val)}
          onClear={() => setTypeId('')}
        />

        <Select
          options={resultsOptions}
          onChange={setStatus}
          placeholder={t('form.status')}
        />

        <DatePicker.RangePicker
          className="full-width"
          format={[dayMonthYearBackSlash, dayMonthYearBackSlash]}
          onChange={(_, datesString) => setDate(datesString)}
          placeholder={[
            t('notifications.from_date'),
            t('notifications.to_date')
          ]}
        />

        <Select
          options={orderingOptions}
          onSelect={(opt) => setOrdering(opt)}
          placeholder={t('common.sort_by')}
        />
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
